import { useEffect, useState, useMemo } from "react";
import Grid from "@mui/material/Grid";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "../../../examples/Tables/DataTable";
import data from "../../dashboard/components/Projects/data";
import useApi from "../../../components/Utils/api";
import toast from "utils/toast";
import { DateRangePicker } from "rsuite";
import { afterToday } from "rsuite/cjs/DateRangePicker/disabledDateUtils";
import reportsLineChartData from "../../dashboard/data/reportsLineChartData";
import Papa from 'papaparse';

function VoidTransactions() {
  const { voidTransactionsReportsColumns } = data();
  const { predefinedRanges } = reportsLineChartData;
  const [transactions, setTransactions] = useState([]);
  const [statusFilter, setStatusFilter] = useState("All");

  const filteredTransactions = useMemo(() => {
    return transactions.filter(row => {
      const isApproved = row.responseCode === '000' || row.responseCode === '00';
      if (statusFilter === "Success" && !isApproved) return false;
      if (statusFilter === "Failed" && isApproved) return false;
      return true;
    });
  }, [transactions, statusFilter]);

  const getTodayDateAtMidnight = () => {
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    const offset = now.getTimezoneOffset();
    now.setMinutes(now.getMinutes() - offset);
    return now;
  };

  const [lastMonthDateRange, setLastMonthDateRange] = useState([
    getTodayDateAtMidnight().toISOString().slice(0, 10),
    getTodayDateAtMidnight().toISOString().slice(0, 10)
  ]);

  const api = useApi();

  const formatDate = (date) => {
    const isoDate = new Date(date.toLocaleString("en-US", { timeZone: "Australia/Sydney" })).toISOString();
    return isoDate.slice(0, 10);
  };

  const getTransactions = (dateRange) => {
    if (dateRange == null) return;
    setLastMonthDateRange(dateRange);
    const data = {
      fromDate: formatDate(dateRange[0]),
      toDate: formatDate(dateRange[1])
    };
    api.post("reports/getvoidtransaction", data)
      .then((response) => {
        if (response.data.statusCodeValue === 200) {
          setTransactions(response.data.body);
        } else {
          setTransactions([]);
          toast.error("No Record Found");
        }
      })
      .catch((error) => {
        if (error.response && error.response.status === 403) {
          toast.error("You do not have permission to perform this action.");
          return;
        }
        setTransactions([]);
        toast.error("No Record Found");
      });
  };

  const generateCSV = () => {
    if (transactions.length === 0) return '';
    const headers = voidTransactionsReportsColumns.map(column => column.Header);
    const rows = transactions.map(transaction =>
      voidTransactionsReportsColumns.map(column => {
        const accessor = column.accessor;
        return typeof accessor === 'function' ? accessor(transaction) : transaction[accessor];
      })
    );
    return Papa.unparse([headers, ...rows], { quotes: true });
  };

  const handleDownloadCSV = () => {
    if (transactions.length === 0) {
      toast.error("No records found to download.");
      return;
    }
    const csvData = generateCSV();
    const dateTimeString = `${new Date().toISOString().slice(0, 10)}_${new Date().toLocaleTimeString().replace(/:/g, '-')}`;
    const blob = new Blob([csvData], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `VoidTransactions_${dateTimeString}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  useEffect(() => {
    getTransactions(lastMonthDateRange);
  }, [lastMonthDateRange]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox mt={6} mb={3}>
        <Grid container spacing={3} justifyContent="center">
          <Grid item xs={12} lg={12}>
            <MDBox display="flex" alignItems="center" justifyContent="space-between" gap={2} mb={2} flexWrap="wrap">
              <MDBox display="flex" alignItems="center" gap={2} flexWrap="wrap">
                <FormControl
                  size="small"
                  sx={{
                    minWidth: 160,
                    "& .MuiOutlinedInput-root": { height: "36.5px" },
                    "& .MuiInputLabel-root": { top: "-4px" },
                    "& .MuiInputLabel-shrink": { top: 0 },
                  }}
                >
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={statusFilter}
                    label="Status"
                    onChange={e => setStatusFilter(e.target.value)}
                  >
                    <MenuItem value="All">All</MenuItem>
                    <MenuItem value="Success">Approved</MenuItem>
                    <MenuItem value="Failed">Declined</MenuItem>
                  </Select>
                </FormControl>

                <DateRangePicker
                  shouldDisableDate={afterToday()}
                  ranges={predefinedRanges}
                  onChange={(date) => setLastMonthDateRange(date)}
                  format="dd MMMM yyyy"
                  value={Object.keys(lastMonthDateRange).length !== 0 ? [new Date(lastMonthDateRange[0]), new Date(lastMonthDateRange[1])] : []}
                />
              </MDBox>

              <MDButton variant="gradient" onClick={handleDownloadCSV} color="info">
                Export
              </MDButton>
            </MDBox>

            <DataTable
              table={{ columns: voidTransactionsReportsColumns, rows: filteredTransactions }}
              showTotalEntries={true}
              isSorted={true}
              noEndBorder
              entriesPerPage={true}
              canSearch={true}
              getRowStyle={row => row.status === '01' || row.status === '02'
                ? { textDecoration: 'line-through', opacity: 0.6 }
                : {}
              }
            />
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default VoidTransactions;
